/** The editing session around one record's draft — page-level, inside the
 *  `DraftProvider`, shared by every FieldSlot and the Ledger (plan §1.3–§1.4, §5).
 *
 *  It holds what is not a field value but belongs to the transaction: the answers to
 *  the changeset's choice questions (an answer that `reverts` takes back the changes that
 *  raise its question instead; an answer a `value` option confirms is bound to the values
 *  that raised it, and dropped at their first change — `choices.ts`), the update-events
 *  policy, the description, the errors a 400 filed under fields, unparseable inputs, the
 *  debounced preview and the Close-editor prompt. It also carries the view-agnostic plumbing FieldSlots and the
 *  ledger meet through: the catalog by key, each field's widget definition (its input and
 *  its protocol — `widgetOf`), value formatting and checks, names of picked records, and a
 *  focus registry (a ledger row focuses its field).
 *
 *  - **Preview**: the request body (the draft's changes plus the choice answers — never
 *    the description, which plans nothing) is debounced 600 ms after the last change
 *    and handed to `config.usePreview`. `preview.data` is the plan of the CURRENT body
 *    only; while the next one loads the ledger shows the catalog estimate instead. A
 *    body the hook asks nothing for (`isEnabled: false`: only rows still being typed) is
 *    `preview.idle` — settled, nothing is loading. `flush()` skips the wait (Save does).
 *  - **Drift seen by the preview** (a `drift` conflict) rebases the entry at once, the
 *    same stale state a 409 produces — Save is blocked until Keep mine / Take theirs.
 *  - **Errors** from a 400 (preview or apply) stay on their field only while the field
 *    still holds the value that was refused; editing it clears the error. `conflictsFor`
 *    hands the same with their codes, for a suggested fix (`config.fixFor`). A save's
 *    form-level errors (keys that are no change: `options.*`, `description`, `changes`)
 *    describe that attempt: they are shown until the draft or the description changes, or
 *    the next save starts, and never block Save — retrying is a save. A preview's describe
 *    the current body and block it.
 *  - **Receipt** of the last save — the ledger's, or a one-step "… now" — lives here so
 *    either can show it (`RecordActionsProvider` sets it too).
 */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';

import {
  actionPath,
  changeKey,
  compileChanges,
  draftValuesEqual,
  freshFromDrift,
  isActionPath,
  parseRecordKey,
  ACTION_PREFIX,
  type DraftLabelFormatter,
  type RecordKey,
} from '../draft/changeSet';
import { useDraft, useDraftSummary } from '../draft/DraftProvider';
import { BUILTIN_WIDGETS } from '../fields/widgets';

import {
  anchorKey,
  bindAnswer,
  bindingHolds,
  choiceExtras,
  confirmedValues,
  confirmersOf,
  optionDefault,
  raisedChoices,
  triggerTargets,
  UPDATE_EVENTS_OPTION,
  type ChoiceAnswers,
  type ChoiceBinding,
  type RaisedChoice,
} from './choices';
import { RecordEditorContext } from './context';
import { asValidationFailure, isChangeErrorKey, type ValidationFailure } from './errors';
import { fieldIndex, fieldOf as lookupField } from './fieldIndex';
import { formatFieldValue, validateFieldValue } from './format';
import type { LedgerReceiptData } from '../ledger/LedgerReceipt';

import type {
  RecordChangeset,
  RecordEditorConfig,
  RecordEditorMode,
  RecordField,
  RecordFieldConflict,
  RecordOption,
  RecordOptionRef,
  RecordPreview,
  RecordPreviewQuery,
  RecordWidget,
} from './types';

export { useRecordEditor } from './context';

export const PREVIEW_DEBOUNCE_MS = 600;

export interface RecordFormError {
  key: string;
  message: string;
  /** `apply`: the last save's refusal — shown until the draft or the description changes
   *  or the next save starts, never blocking Save. `preview`: the current body's — it
   *  blocks Save. */
  source: 'apply' | 'preview';
}

export interface RecordPreviewState {
  query: RecordPreviewQuery;
  /** The plan of the current body; null while the debounce runs or the request loads. */
  data: RecordPreview | null;
  /** A newer body than the one previewed (debouncing or loading). */
  pending: boolean;
  /** The current body has nothing the preview can plan yet (the hook asked nothing, see
   *  `RecordPreviewQuery.isEnabled`): settled — no plan is on its way. */
  idle: boolean;
  /** The preview of the current body failed with something other than a 400. */
  failed: boolean;
  flush: () => void;
}

export interface RecordEditorContextValue {
  mode: RecordEditorMode;
  config: RecordEditorConfig;
  /** The catalog by key. A key two entities share (the itinerary's `description` and its
   *  days', O10) is not in it: `fieldOf` finds it by its entity. */
  fieldByKey: ReadonlyMap<string, RecordField>;
  /** The catalog field `key` of `entity` — a FieldSlot's, a cell's (`fieldIndex.fieldOf`). */
  fieldOf: (entity: string, key: string) => RecordField | undefined;
  /** The catalog field a draft path shows (`action:checkin` → checkin_status); `rec`, the
   *  path's record, tells a key two entities share apart. */
  fieldForPath: (path: string, rec?: RecordKey) => RecordField | undefined;
  /** The draft path of a catalog field (an action field travels as `action:<key>`). */
  pathOf: (field: RecordField) => string;
  actionKeys: ReadonlySet<string>;
  /** A field's widget definition: the record kind's own (`config.widgets`), else the
   *  built-in one of that name; undefined for a field with no input. The editor and the
   *  ledger reach a widget's protocol (its format, checks and ledger lines) through it. */
  widgetOf: (field: RecordField) => RecordWidget | undefined;
  formatValue: (field: RecordField, rec: RecordKey, value: unknown) => string;
  /** Why a change's value cannot be saved (`validateFieldValue` with the field's widget),
   *  or null. `from`: the change's baseline. */
  validateValue: (field: RecordField, to: unknown, from?: unknown) => string | null;
  /** History labels ("Date Fri 2 Oct → Sat 3 Oct") for the draft's undo / redo. */
  labelFormatter: DraftLabelFormatter;
  optionRef: (fieldKey: string, id: unknown) => RecordOptionRef | undefined;
  /** Widgets call this with the records they offer, so a pick is shown by name. */
  rememberOptions: (fieldKey: string, refs: RecordOptionRef[]) => void;
  registerFocus: (rec: RecordKey, path: string, focus: () => void) => () => void;
  focusField: (rec: RecordKey, path: string) => void;

  answers: ChoiceAnswers;
  setAnswer: (key: string, value: string | boolean) => void;
  raised: RaisedChoice[];
  /** The Emit · Suppress switch's option (null when the catalog has none). */
  updateEventsOption: RecordOption | null;
  updateEvents: string | null;
  setUpdateEvents: (value: string) => void;
  /** The entity's event switch (guest_events), null when the record kind has none. */
  entitySwitch: EntityEventSwitch | null;
  setEntitySwitch: (value: string) => void;

  /** The request body without the description; null when nothing to send. */
  body: RecordChangeset | null;
  preview: RecordPreviewState;

  /** Why this field's value cannot be saved: server errors for the value it holds, a
   *  preview conflict, or an unparseable input. */
  errorsFor: (rec: RecordKey, path: string) => string[];
  /** The same server errors and preview conflicts with their codes (no client checks). */
  conflictsFor: (rec: RecordKey, path: string) => RecordFieldConflict[];
  invalidInputs: Readonly<Record<string, string>>;
  setInvalidInput: (rec: RecordKey, path: string, message: string | null) => void;
  /** 400 errors not filed under a field (options.*, description, changes). */
  formErrors: RecordFormError[];
  setApplyFailure: (failure: ValidationFailure | null, body: RecordChangeset | null) => void;

  closePrompt: boolean;
  /** Close editor: a clean draft exits; a dirty one asks (Save · Discard · Keep editing). */
  requestClose: () => void;
  cancelClose: () => void;
  exitEdit: () => void;
  /** After a save: the answers, policy, description and errors belonged to it. */
  resetSession: () => void;
  /** The last save's receipt; cleared when the next draft starts (the ledger does). */
  receipt: LedgerReceiptData | null;
  setReceipt: (receipt: LedgerReceiptData | null) => void;
}

/** The entity's event switch as the ledger renders it (`config.entityEventSwitch`). */
export interface EntityEventSwitch {
  option: RecordOption;
  entity: string;
  /** The ledger group it prefers (`guest`); null = the first group with that entity. */
  group: string | null;
  value: string;
  valueLabel: (value: string) => string;
  /** The draft changes that entity — the switch is shown and sent. */
  applies: boolean;
}

interface DescriptionCtx {
  description: string;
  setDescription: (value: string) => void;
}

/** Separate, so typing the description re-renders the ledger only. */
const DescriptionContext = createContext<DescriptionCtx | null>(null);

export interface RecordEditorProviderProps {
  mode: RecordEditorMode;
  config: RecordEditorConfig;
  /** Leave edit mode (the page's `?edit` state). */
  onExitEdit?: () => void;
  children: ReactNode;
}

function refKey(fieldKey: string, id: unknown): string {
  return `${fieldKey}|${String(id)}`;
}

/** The value each change of `body` carries, by change key. */
function sentValues(body: RecordChangeset | null): Map<string, unknown> {
  const out = new Map<string, unknown>();
  for (const c of body?.changes ?? []) {
    out.set(`${c.entity}:${c.id}:${c.field ?? c.action ?? ''}`, c.to);
  }
  return out;
}

interface KeyedBody {
  key: string;
  changesKey: string;
  body: RecordChangeset | null;
}

export function RecordEditorProvider({ mode, config, onExitEdit, children }: RecordEditorProviderProps) {
  const { state, dispatch } = useDraft();
  const { count } = useDraftSummary();

  // ---- catalog ------------------------------------------------------------
  const index = useMemo(() => fieldIndex(config.fields), [config.fields]);
  const fieldByKey = index.byKey;
  const fieldOf = useCallback((entity: string, key: string) => lookupField(index, entity, key), [index]);
  const fieldByAction = useMemo(() => {
    const out = new Map<string, RecordField>();
    for (const f of config.fields) if (f.kind === 'action' && f.action) out.set(f.action, f);
    return out;
  }, [config.fields]);
  const actionKeys = useMemo(
    () => new Set(config.actions.filter((a) => a.change === 'action').map((a) => a.key)),
    [config.actions],
  );
  const fieldForPath = useCallback(
    (path: string, rec?: RecordKey) =>
      isActionPath(path)
        ? fieldByAction.get(path.slice(ACTION_PREFIX.length))
        : rec === undefined
          ? fieldByKey.get(path)
          : fieldOf(parseRecordKey(rec).entity, path),
    [fieldByAction, fieldByKey, fieldOf],
  );
  const pathOf = useCallback(
    (field: RecordField) =>
      field.kind === 'action' && field.action && actionKeys.has(field.action)
        ? actionPath(field.action)
        : field.key,
    [actionKeys],
  );

  // ---- picked-record names ------------------------------------------------
  const refs = useRef(new Map<string, RecordOptionRef>());
  const seeded = useRef<unknown>(null);
  if (seeded.current !== config.optionRefs) {
    seeded.current = config.optionRefs;
    for (const [fieldKey, list] of Object.entries(config.optionRefs ?? {})) {
      for (const r of list) refs.current.set(refKey(fieldKey, r.id), r);
    }
  }
  const optionRef = useCallback(
    (fieldKey: string, id: unknown) => refs.current.get(refKey(fieldKey, id)),
    [],
  );
  const rememberOptions = useCallback((fieldKey: string, list: RecordOptionRef[]) => {
    for (const r of list) refs.current.set(refKey(fieldKey, r.id), r);
  }, []);

  const { timezone, formatValue: formatOverride, widgets: ownWidgets } = config;
  const widgetOf = useCallback(
    (field: RecordField): RecordWidget | undefined =>
      field.widget ? (ownWidgets?.[field.widget] ?? BUILTIN_WIDGETS[field.widget]) : undefined,
    [ownWidgets],
  );
  const formatValue = useCallback(
    (field: RecordField, rec: RecordKey, value: unknown) =>
      formatOverride?.(field, rec, value) ?? formatFieldValue(field, value, { timezone, optionRef }, widgetOf(field)),
    [formatOverride, timezone, optionRef, widgetOf],
  );
  const validateValue = useCallback(
    (field: RecordField, to: unknown, from?: unknown) => validateFieldValue(field, to, from, widgetOf(field)),
    [widgetOf],
  );
  const labelFormatter = useMemo<DraftLabelFormatter>(
    () => ({
      field: (rec, path) => fieldForPath(path, rec)?.label ?? path,
      value: (rec, path, value) => {
        const field = fieldForPath(path, rec);
        return field ? formatValue(field, rec, value) : String(value ?? '—');
      },
    }),
    [fieldForPath, formatValue],
  );

  // ---- focus registry -----------------------------------------------------
  const focusHandles = useRef(new Map<string, () => void>());
  const registerFocus = useCallback((rec: RecordKey, path: string, focus: () => void) => {
    const k = anchorKey(rec, path);
    focusHandles.current.set(k, focus);
    return () => {
      if (focusHandles.current.get(k) === focus) focusHandles.current.delete(k);
    };
  }, []);
  const { onFocusMissing } = config;
  const focusField = useCallback(
    (rec: RecordKey, path: string) => {
      const handle = focusHandles.current.get(anchorKey(rec, path));
      if (handle) handle();
      else onFocusMissing?.(rec, path);
    },
    [onFocusMissing],
  );

  // ---- choices ------------------------------------------------------------
  const updateEventsOption = useMemo(
    () => config.options.find((o) => o.key === UPDATE_EVENTS_OPTION) ?? null,
    [config.options],
  );
  const defaultUpdateEvents = useMemo(() => {
    if (!updateEventsOption) return null;
    const d = config.optionDefaults?.[UPDATE_EVENTS_OPTION] ?? updateEventsOption.default;
    return typeof d === 'string' ? d : (String(updateEventsOption.values[0]?.value ?? '') || null);
  }, [updateEventsOption, config.optionDefaults]);
  const [answers, setAnswers] = useState<ChoiceAnswers>({});
  // The answers a `value` option confirms, each bound to the draft values it was given for.
  const confirmers = useMemo(() => confirmersOf(config.options), [config.options]);
  const [bindings, setBindings] = useState<Readonly<Record<string, ChoiceBinding>>>({});
  const [updateEventsPick, setUpdateEventsPick] = useState<string | null>(null);
  const updateEvents = updateEventsPick ?? defaultUpdateEvents;
  // An answer that `reverts` takes back the changes that raise its question (one undoable
  // step) instead of being kept: the question goes with them, and nothing is sent.
  const answering = useRef({ options: config.options, state, confirmers });
  answering.current = { options: config.options, state, confirmers };
  const setAnswer = useCallback(
    (key: string, value: string | boolean) => {
      const { options, state: now, confirmers: confirmed } = answering.current;
      const option = options.find((o) => o.key === key);
      const picked = option?.values.find((v) => v.value === value);
      if (option && picked?.reverts) {
        setAnswers(({ [key]: _dropped, ...rest }) => rest);
        setBindings(({ [key]: _unbound, ...rest }) => rest);
        const targets = triggerTargets(option, now);
        if (targets.length > 0) dispatch({ type: 'REVERT', targets, label: `${option.label}: ${picked.label}` });
        return;
      }
      if (option && confirmed.has(key)) setBindings((b) => ({ ...b, [key]: bindAnswer(option, now) }));
      setAnswers((a) => ({ ...a, [key]: value }));
    },
    [dispatch],
  );
  // A bound answer holds only while the draft holds the values it was given for: any change
  // of them unanswers the question (Save waits, the field and the ledger ask again) — in this
  // render already, and for good: a value typed back needs a fresh answer.
  const brokenBindings = useMemo(
    () =>
      Object.keys(bindings).filter((key) => {
        const option = config.options.find((o) => o.key === key);
        return !option || !bindingHolds(bindings[key]!, option, state);
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [bindings, config.options, state.version],
  );
  const heldAnswers = useMemo<ChoiceAnswers>(() => {
    const unbound = new Set([...confirmers.keys()].filter((k) => !bindings[k] || brokenBindings.includes(k)));
    if (![...unbound].some((k) => k in answers)) return answers;
    return Object.fromEntries(Object.entries(answers).filter(([k]) => !unbound.has(k)));
  }, [answers, bindings, brokenBindings, confirmers]);
  useEffect(() => {
    if (brokenBindings.length === 0) return;
    const drop = <T,>(rec: Readonly<Record<string, T>>) =>
      Object.fromEntries(Object.entries(rec).filter(([k]) => !brokenBindings.includes(k)));
    setAnswers((a) => drop(a));
    setBindings((b) => drop(b));
  }, [brokenBindings]);

  // The entity's event switch (guest_events): its value, and whether the draft changes
  // that entity — only then is it shown and sent.
  const switchDef = config.entityEventSwitch;
  const switchOption = useMemo(
    () => (switchDef ? (config.options.find((o) => o.key === switchDef.option) ?? null) : null),
    [config.options, switchDef],
  );
  const [switchPick, setSwitchPick] = useState<string | null>(null);
  const switchDefault = switchOption ? optionDefault(switchOption, config.optionDefaults) : null;
  const switchValue =
    switchPick ?? (typeof switchDefault === 'string' ? switchDefault : (String(switchOption?.values[0]?.value ?? '') || null));
  const switchEntity = switchDef?.entity;
  const switchApplies = useMemo(
    () => Boolean(switchEntity) && Object.keys(state.entries).some((rec) => rec.startsWith(`${switchEntity}:`)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [state.version, switchEntity],
  );
  const entitySwitch = useMemo<EntityEventSwitch | null>(
    () =>
      switchDef && switchOption && switchValue
        ? {
            option: switchOption,
            entity: switchDef.entity,
            group: switchDef.group ?? null,
            value: switchValue,
            valueLabel: (v: string) =>
              switchDef.valueLabels?.[v] ?? switchOption.values.find((x) => String(x.value) === v)?.label ?? v,
            applies: switchApplies,
          }
        : null,
    [switchDef, switchOption, switchValue, switchApplies],
  );
  const switchSkip = useMemo(() => new Set(switchDef ? [switchDef.option] : []), [switchDef]);

  const changes = useMemo(
    () => compileChanges(state),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [state.version],
  );
  const changesKey = useMemo(() => JSON.stringify(changes), [changes]);

  // ---- debounced preview --------------------------------------------------
  // `raised` reads the preview only when it planned the same CHANGES as the draft holds
  // now — the questions a changeset raises do not depend on their answers, and keying on
  // the changes alone keeps answers → body → preview → questions from looping.
  const [debounced, setDebounced] = useState<KeyedBody>({ key: '', changesKey: '', body: null });
  const query = config.usePreview(debounced.body);
  const plannedNow =
    query.data !== undefined && !query.isPlaceholderData && debounced.body !== null ? query.data : null;

  const { isOptionAsked, optionDefaults, recordOrder, resolveOption } = config;
  const raised = useMemo(
    () =>
      raisedChoices({
        options: config.options,
        state,
        answers: heldAnswers,
        optionDefaults,
        recordOrder,
        isOptionAsked,
        previewChoices:
          plannedNow && debounced.changesKey === changesKey ? plannedNow.required_choices : null,
        actionKeys,
        skip: switchSkip,
      }).map((c) => (resolveOption ? { ...c, option: resolveOption(c.option, state) } : c)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      config.options,
      state.version,
      heldAnswers,
      optionDefaults,
      recordOrder,
      isOptionAsked,
      resolveOption,
      plannedNow,
      debounced.changesKey,
      changesKey,
      actionKeys,
      switchSkip,
    ],
  );

  const body = useMemo<RecordChangeset | null>(() => {
    if (mode !== 'edit' || changes.length === 0) return null;
    const switches = entitySwitch?.applies ? [{ path: entitySwitch.option.path, answer: entitySwitch.value }] : [];
    const confirmed = confirmedValues(raised, confirmers, (key) => bindings[key]);
    return { changes, ...choiceExtras(raised, updateEvents, switches, confirmed) } as RecordChangeset;
  }, [mode, changes, raised, updateEvents, entitySwitch, confirmers, bindings]);
  const bodyKey = useMemo(() => (body ? JSON.stringify(body) : ''), [body]);

  const latest = useRef<KeyedBody>({ key: bodyKey, changesKey, body });
  latest.current = { key: bodyKey, changesKey, body };
  useEffect(() => {
    if (debounced.key === bodyKey) return undefined;
    // Leaving edit mode, or emptying the draft, stops previewing at once.
    if (!body) {
      setDebounced({ key: '', changesKey: '', body: null });
      return undefined;
    }
    const t = window.setTimeout(() => setDebounced(latest.current), PREVIEW_DEBOUNCE_MS);
    return () => window.clearTimeout(t);
  }, [bodyKey, body, debounced.key]);
  const flush = useCallback(() => {
    setDebounced((d) => (d.key === latest.current.key ? d : latest.current));
  }, []);

  const previewCurrent = body !== null && debounced.key === bodyKey;
  const previewData = previewCurrent ? plannedNow : null;
  const previewError = previewCurrent ? query.error : null;
  const previewFailure = useMemo(
    () => (previewError ? asValidationFailure(previewError) : null),
    [previewError],
  );
  const previewIdle = previewCurrent && query.isEnabled === false;
  const previewPending = body !== null && !previewIdle && (!previewCurrent || (query.isFetching && !previewData));
  const previewFailed = Boolean(previewError) && !previewFailure;
  const preview = useMemo<RecordPreviewState>(
    () => ({ query, data: previewData, pending: previewPending, idle: previewIdle, failed: previewFailed, flush }),
    // `query` is a fresh object every render; what the ledger reads of it is below.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [query.data, query.isFetching, query.error, previewData, previewPending, previewIdle, previewFailed, flush],
  );

  // Drift the preview found: the same stale state a 409 produces.
  useEffect(() => {
    const drift = plannedNow?.conflicts.filter((c) => c.code === 'drift') ?? [];
    if (drift.length === 0) return;
    const drifted = drift.flatMap((c) => {
      const parts = c.key.split(':');
      const [entity, id, ...rest] = parts;
      const name = rest.join(':');
      if (!entity || !id || !name) return [];
      const isAction = actionKeys.has(name);
      return [
        {
          entity,
          id: Number(id),
          ...(isAction ? { action: name } : { field: name }),
          current: c.current ?? null,
          changed_by: c.changed_by ?? null,
          // A row deleted on the server meanwhile (an itinerary's day): the change can only go.
          ...(c.gone ? { gone: true } : {}),
        },
      ];
    });
    dispatch({ type: 'REBASE', fresh: freshFromDrift(drifted) });
  }, [plannedNow, actionKeys, dispatch]);

  // ---- errors -------------------------------------------------------------
  const [applyFailure, setApplyFailureState] = useState<{
    failure: ValidationFailure;
    sent: Map<string, unknown>;
    /** The draft's version the save was refused at: its form-level errors are about that
     *  draft only. */
    version: number;
    /** The description changed since: the form-level errors no longer describe it. */
    formStale: boolean;
  } | null>(null);
  const versionRef = useRef(state.version);
  versionRef.current = state.version;
  const setApplyFailure = useCallback(
    (failure: ValidationFailure | null, sentBody: RecordChangeset | null) =>
      setApplyFailureState(
        failure ? { failure, sent: sentValues(sentBody), version: versionRef.current, formStale: false } : null,
      ),
    [],
  );
  // The description is part of what a save sends: editing it voids the last refusal's
  // form-level errors (one state update, then a no-op per keystroke).
  const onDescriptionEdit = useCallback(
    () => setApplyFailureState((cur) => (cur && !cur.formStale ? { ...cur, formStale: true } : cur)),
    [],
  );
  const [invalidInputs, setInvalidInputs] = useState<Record<string, string>>({});
  const setInvalidInput = useCallback((rec: RecordKey, path: string, message: string | null) => {
    const k = anchorKey(rec, path);
    setInvalidInputs((cur) => {
      if (message === null) {
        if (!(k in cur)) return cur;
        const next = { ...cur };
        delete next[k];
        return next;
      }
      return cur[k] === message ? cur : { ...cur, [k]: message };
    });
  }, []);

  const previewSent = useMemo(() => sentValues(debounced.body), [debounced.body]);
  const errorsFor = useCallback(
    (rec: RecordKey, path: string): string[] => {
      const key = changeKey(rec, path);
      const entry = state.entries[rec]?.[path];
      const holds = (sent: Map<string, unknown>) =>
        entry !== undefined && sent.has(key) && draftValuesEqual(sent.get(key), entry.to);
      const out: string[] = [];
      if (applyFailure && holds(applyFailure.sent)) {
        for (const e of applyFailure.failure.errors[key] ?? []) out.push(e.message);
      }
      if (previewFailure && holds(previewSent)) {
        for (const e of previewFailure.errors[key] ?? []) out.push(e.message);
      }
      if (plannedNow && holds(previewSent)) {
        for (const c of plannedNow.conflicts) {
          if (c.key === key && c.code !== 'drift') out.push(c.detail);
        }
      }
      const invalid = invalidInputs[anchorKey(rec, path)];
      if (invalid) out.push(invalid);
      return [...new Set(out)];
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [state.version, applyFailure, previewFailure, plannedNow, previewSent, invalidInputs],
  );

  const conflictsFor = useCallback(
    (rec: RecordKey, path: string): RecordFieldConflict[] => {
      const key = changeKey(rec, path);
      const entry = state.entries[rec]?.[path];
      const holds = (sent: Map<string, unknown>) =>
        entry !== undefined && sent.has(key) && draftValuesEqual(sent.get(key), entry.to);
      const out: RecordFieldConflict[] = [];
      for (const [failure, sent] of [
        [applyFailure?.failure ?? null, applyFailure?.sent],
        [previewFailure, previewSent],
      ] as const) {
        if (!failure || !sent || !holds(sent)) continue;
        for (const e of failure.errors[key] ?? []) {
          out.push({
            code: e.code,
            message: e.message,
            owner_user_id: e.owner_user_id ?? null,
            suggested_action: e.suggested_action ?? null,
            contact_key: e.contact_key ?? null,
          });
        }
      }
      if (plannedNow && holds(previewSent)) {
        for (const c of plannedNow.conflicts) {
          if (c.key === key && c.code !== 'drift') {
            out.push({
              code: c.code,
              message: c.detail,
              owner_user_id: c.owner_user_id ?? null,
              suggested_action: c.suggested_action ?? null,
              contact_key: c.contact_key ?? null,
            });
          }
        }
      }
      return out;
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [state.version, applyFailure, previewFailure, plannedNow, previewSent],
  );

  // A save's form-level errors describe the draft and description it sent: a change to
  // either voids them (field errors clear on their own, by value).
  const applyFormCurrent = applyFailure !== null && !applyFailure.formStale && applyFailure.version === state.version;
  const formErrors = useMemo<RecordFormError[]>(() => {
    const out: RecordFormError[] = [];
    const add = (f: ValidationFailure | null, source: RecordFormError['source']) => {
      if (!f) return;
      for (const [key, errs] of Object.entries(f.errors)) {
        if (isChangeErrorKey(key)) continue;
        for (const e of errs) out.push({ key, message: e.message, source });
      }
      if (Object.keys(f.errors).length === 0) out.push({ key: 'changes', message: f.message, source });
    };
    add(applyFormCurrent ? applyFailure.failure : null, 'apply');
    add(previewFailure, 'preview');
    const seen = new Set<string>();
    return out.filter((e) => {
      const k = `${e.key}|${e.message}`;
      if (seen.has(k)) return false;
      seen.add(k);
      return true;
    });
  }, [applyFormCurrent, applyFailure, previewFailure]);

  // ---- close flow ---------------------------------------------------------
  const [closePrompt, setClosePrompt] = useState(false);
  const exitEdit = useCallback(() => {
    setClosePrompt(false);
    onExitEdit?.();
  }, [onExitEdit]);
  const requestClose = useCallback(() => {
    if (count === 0) exitEdit();
    else setClosePrompt(true);
  }, [count, exitEdit]);
  const cancelClose = useCallback(() => setClosePrompt(false), []);
  useEffect(() => {
    if (mode !== 'edit') setClosePrompt(false);
  }, [mode]);

  const [receipt, setReceipt] = useState<LedgerReceiptData | null>(null);

  // The description lives in its own provider below; a reset reaches it by token.
  const [descriptionReset, setDescriptionReset] = useState(0);
  const resetSession = useCallback(() => {
    setAnswers({});
    setBindings({});
    setUpdateEventsPick(null);
    setSwitchPick(null);
    setDescriptionReset((n) => n + 1);
    setApplyFailureState(null);
    setInvalidInputs({});
  }, []);

  const value = useMemo<RecordEditorContextValue>(
    () => ({
      mode,
      config,
      fieldByKey,
      fieldOf,
      fieldForPath,
      pathOf,
      actionKeys,
      widgetOf,
      formatValue,
      validateValue,
      labelFormatter,
      optionRef,
      rememberOptions,
      registerFocus,
      focusField,
      answers: heldAnswers,
      setAnswer,
      raised,
      updateEventsOption,
      updateEvents,
      setUpdateEvents: setUpdateEventsPick,
      entitySwitch,
      setEntitySwitch: setSwitchPick,
      body,
      preview,
      errorsFor,
      conflictsFor,
      invalidInputs,
      setInvalidInput,
      formErrors,
      setApplyFailure,
      closePrompt,
      requestClose,
      cancelClose,
      exitEdit,
      resetSession,
      receipt,
      setReceipt,
    }),
    [
      mode,
      config,
      fieldByKey,
      fieldOf,
      fieldForPath,
      pathOf,
      actionKeys,
      widgetOf,
      formatValue,
      validateValue,
      labelFormatter,
      optionRef,
      rememberOptions,
      registerFocus,
      focusField,
      heldAnswers,
      setAnswer,
      raised,
      updateEventsOption,
      updateEvents,
      entitySwitch,
      body,
      preview,
      errorsFor,
      conflictsFor,
      invalidInputs,
      setInvalidInput,
      formErrors,
      setApplyFailure,
      closePrompt,
      requestClose,
      cancelClose,
      exitEdit,
      resetSession,
      receipt,
    ],
  );
  return (
    <RecordEditorContext.Provider value={value}>
      <DescriptionProvider resetToken={descriptionReset} onEdit={onDescriptionEdit}>
        {children}
      </DescriptionProvider>
    </RecordEditorContext.Provider>
  );
}

function DescriptionProvider({
  resetToken,
  onEdit,
  children,
}: {
  resetToken: number;
  /** The operator typed in it (not a reset). Must be stable. */
  onEdit: () => void;
  children: ReactNode;
}) {
  const [description, setRaw] = useState('');
  useEffect(() => {
    if (resetToken > 0) setRaw('');
  }, [resetToken]);
  const setDescription = useCallback(
    (v: string) => {
      setRaw(v);
      onEdit();
    },
    [onEdit],
  );
  const value = useMemo(() => ({ description, setDescription }), [description, setDescription]);
  return <DescriptionContext.Provider value={value}>{children}</DescriptionContext.Provider>;
}

export function useRecordDescription(): DescriptionCtx {
  const v = useContext(DescriptionContext);
  if (!v) throw new Error('useRecordDescription must be used inside <RecordEditorProvider>');
  return v;
}
